import React from 'react';
import { 
  Sparkles, 
  Loader2, 
  Download, 
  Edit, 
  Upload, 
  X, 
  FileText, 
  File,
  AlertCircle,
  Info,
  ShieldOff,
  Shield,
  ShieldCheck,
  Save,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { openaiService, type AIPolicyLevel, type LearningOutcome } from '@/services/openaiService';
import { extractTextFromFile, validateFile } from '@/utils/fileProcessing';
import { exportToDocx } from '@/utils/exportDocx';
import { OutcomesInput } from './OutcomesInput';
import { MarkdownEditor, MarkdownEditorRef } from './MarkdownEditor';

const POLICY_LEVELS = [
  {
    value: 'ai-free' as AIPolicyLevel,
    label: 'AI-Free Assignment',
    icon: ShieldOff,
    color: 'text-brand-red',
    bgColor: 'bg-brand-red-50 dark:bg-brand-red-950',
    description: 'Students complete work independently without AI assistance. Develops independent thought, critical thinking, and original expression.',
  },
  {
    value: 'ai-scaffolded' as AIPolicyLevel,
    label: 'AI-Scaffolded Assignment',
    icon: Shield,
    color: 'text-brand-yellow-600',
    bgColor: 'bg-brand-yellow-50 dark:bg-brand-yellow-950',
    description: 'AI tools permitted at specific stages (planning and revision) only. Core work must be completed independently.',
  },
  {
    value: 'ai-integrated' as AIPolicyLevel,
    label: 'AI-Integrated Assignment',
    icon: ShieldCheck,
    color: 'text-accent-green',
    bgColor: 'bg-accent-green/10 dark:bg-accent-green/20',
    description: 'Students work collaboratively with AI tools throughout. Must maintain control, ethical standards, and transparency.',
  },
];

export const PolicyGenerator: React.FC = () => {
  const [taskInputMode, setTaskInputMode] = React.useState<'text' | 'upload'>('text');
  const [taskText, setTaskText] = React.useState('');
  const [uploadedFile, setUploadedFile] = React.useState<File | null>(null);
  const [processingFile, setProcessingFile] = React.useState(false);
  const [fileError, setFileError] = React.useState<string | null>(null);
  
  const [outcomes, setOutcomes] = React.useState<LearningOutcome[]>([]);
  const [policyLevel, setPolicyLevel] = React.useState<AIPolicyLevel | ''>('');
  
  const [generatedPolicy, setGeneratedPolicy] = React.useState('');
  const [isGenerating, setIsGenerating] = React.useState(false);
  const [generateError, setGenerateError] = React.useState<string | null>(null);
  const [isEditing, setIsEditing] = React.useState(false);
  const [policyBackup, setPolicyBackup] = React.useState(''); // Backup for cancel functionality
  const [editorSession, setEditorSession] = React.useState(0); // Force re-mount to reset editor state
  
  const editorRef = React.useRef<MarkdownEditorRef>(null);

  // Handle file upload
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileError(null);

    const validation = validateFile(file);
    if (!validation.valid) {
      setFileError(validation.error || 'Invalid file');
      return;
    }

    setUploadedFile(file);
    setProcessingFile(true);

    try {
      const extractedText = await extractTextFromFile(file);
      setTaskText(extractedText);
      console.log(`✅ Extracted ${extractedText.length} characters from ${file.name}`);
    } catch (error) {
      console.error('Failed to process file:', error);
      setFileError(error instanceof Error ? error.message : 'Failed to extract text from file');
      setUploadedFile(null);
    } finally {
      setProcessingFile(false);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setFileError(null);
    setTaskText('');
  };

  // Generate policy
  const handleGenerate = async () => {
    const taskContent = taskInputMode === 'text' ? taskText.trim() : taskText;
    
    if (!taskContent) {
      setGenerateError('Please provide assessment task details');
      return;
    }

    if (!policyLevel) {
      setGenerateError('Please select an AI policy level');
      return;
    }

    if (outcomes.length === 0) {
      setGenerateError('Please add at least one learning outcome');
      return;
    }

    setGenerateError(null);
    setIsGenerating(true);

    try {
      const policy = await openaiService.generateAIPolicy({
        taskDescription: taskContent,
        aiPolicyLevel: policyLevel,
        outcomes,
      });

      setGeneratedPolicy(policy);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to generate policy:', error);
      setGenerateError(error instanceof Error ? error.message : 'Failed to generate policy');
    } finally {
      setIsGenerating(false);
    }
  };

  // Download as DOCX
  const handleDownload = async () => {
    if (!generatedPolicy) return;

    try {
      await exportToDocx(
        generatedPolicy,
        `AI Policy - ${POLICY_LEVELS.find(p => p.value === policyLevel)?.label || 'Generated'}`,
        policyLevel || 'custom'
      );
    } catch (error) {
      console.error('Failed to download:', error);
      alert('Failed to download DOCX. Please try again.');
    }
  };

  const selectedPolicyInfo = POLICY_LEVELS.find(p => p.value === policyLevel);

  // Handle entering edit mode
  const handleStartEditing = () => {
    setPolicyBackup(generatedPolicy); // Store backup before editing
    setIsEditing(true);
  };

  // Handle saving edits
  const handleSaveEdits = () => {
    if (editorRef.current) {
      const newMarkdown = editorRef.current.getMarkdown();
      setGeneratedPolicy(newMarkdown);
    }
    setIsEditing(false);
    setEditorSession((n) => n + 1); // Reset editor to avoid stale content
  };

  // Handle canceling edits
  const handleCancelEdits = () => {
    setGeneratedPolicy(policyBackup); // Restore backup
    setIsEditing(false);
    setEditorSession((n) => n + 1); // Reset editor to avoid stale content
  };

  return (
    <div className="space-y-6 opacity-0 animate-fade-in">
      {/* Step 1: Assignment Input */}
      <Card className="opacity-0 animate-fade-in animate-stagger-1">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold">1</span>
            Assignment Details
          </CardTitle>
          <CardDescription>
            Provide your assessment task description to generate a contextual AI policy
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={taskInputMode} onValueChange={(v) => setTaskInputMode(v as 'text' | 'upload')}>
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="text" className="gap-2">
                <FileText className="h-4 w-4" />
                Manual Entry
              </TabsTrigger>
              <TabsTrigger value="upload" className="gap-2">
                <Upload className="h-4 w-4" />
                Upload File
              </TabsTrigger>
            </TabsList>

            <TabsContent value="text">
              <Textarea
                placeholder="Enter your assessment task description here. Include task objectives, requirements, deliverables, and any other relevant details..."
                value={taskText}
                onChange={(e) => setTaskText(e.target.value)}
                rows={8}
                className="resize-y"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Provide a detailed description of your assessment task to generate a relevant AI policy.
              </p>
            </TabsContent>

            <TabsContent value="upload">
              {!uploadedFile ? (
                <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer">
                  <input
                    type="file"
                    id="task-file-upload"
                    accept=".txt,.docx,.pdf,.md"
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={processingFile}
                  />
                  <label htmlFor="task-file-upload" className="cursor-pointer">
                    <Upload className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-sm font-medium mb-2">
                      {processingFile ? 'Processing file...' : 'Click to upload or drag and drop'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Supports .txt, .docx, .pdf, and .md files (max 10MB)
                    </p>
                  </label>
                </div>
              ) : (
                <div className="border rounded-lg p-4 bg-muted/30">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <File className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{uploadedFile.name}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {(uploadedFile.size / 1024).toFixed(1)} KB
                        </p>
                        {taskText && (
                          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">
                            ✓ Extracted {taskText.length.toLocaleString()} characters
                          </p>
                        )}
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={handleRemoveFile}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              
              {fileError && (
                <div className="mt-4 rounded-md bg-destructive/10 p-3 border border-destructive/20">
                  <p className="text-sm text-destructive flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    {fileError}
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Step 2: Learning Outcomes */}
      <Card className="opacity-0 animate-fade-in animate-stagger-2">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold">2</span>
            Learning Outcomes
          </CardTitle>
          <CardDescription>
            Define the learning outcomes this assessment will evaluate
          </CardDescription>
        </CardHeader>
        <CardContent>
          <OutcomesInput outcomes={outcomes} onChange={setOutcomes} />
        </CardContent>
      </Card>

      {/* Step 3: Policy Level */}
      <Card className="opacity-0 animate-fade-in animate-stagger-3">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="flex items-center justify-center w-7 h-7 rounded-full bg-primary text-primary-foreground text-sm font-bold">3</span>
            AI Policy Level
          </CardTitle>
          <CardDescription>
            Select the appropriate AI use level for your assessment
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Select value={policyLevel} onValueChange={(v) => setPolicyLevel(v as AIPolicyLevel)}>
            <SelectTrigger>
              <SelectValue placeholder="Select AI policy level..." />
            </SelectTrigger>
            <SelectContent>
              {POLICY_LEVELS.map((level) => {
                const Icon = level.icon;
                return (
                  <SelectItem key={level.value} value={level.value}>
                    <div className="flex items-center gap-2">
                      <Icon className={`h-4 w-4 ${level.color}`} />
                      <span>{level.label}</span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {selectedPolicyInfo && (
            <div className={`rounded-lg p-4 ${selectedPolicyInfo.bgColor} border`}>
              <div className="flex items-start gap-3">
                <Info className={`h-5 w-5 mt-0.5 ${selectedPolicyInfo.color}`} />
                <div>
                  <p className="font-medium text-sm">{selectedPolicyInfo.label}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedPolicyInfo.description}
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generate Button */}
      <div className="flex flex-col items-center gap-4 opacity-0 animate-fade-in animate-stagger-4">
        {generateError && (
          <div className="w-full rounded-md bg-destructive/10 p-4 border border-destructive/20">
            <p className="text-sm text-destructive flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {generateError}
            </p>
          </div>
        )}
        
        <Button
          size="lg"
          onClick={handleGenerate}
          disabled={isGenerating || !taskText.trim() || !policyLevel || outcomes.length === 0}
          className="px-8 h-12 text-base shadow-lg hover:shadow-xl transition-all"
        >
          {isGenerating ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Generating Policy...
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-5 w-5" />
              Generate AI Policy
            </>
          )}
        </Button>
        
        <p className="text-xs text-muted-foreground text-center max-w-md">
          The AI will create a customized policy based on your assessment, outcomes, and selected AI use level.
        </p>
      </div>

      {/* Generated Policy */}
      {generatedPolicy && (
        <Card className="opacity-0 animate-fade-in animate-stagger-5">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  Generated AI Policy
                  {selectedPolicyInfo && (
                    <Badge variant="outline" className={selectedPolicyInfo.color}>
                      {selectedPolicyInfo.label}
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Review and edit the generated policy, then download as DOCX
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {isEditing ? (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCancelEdits}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveEdits}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleStartEditing}
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button onClick={handleDownload} className="gap-2">
                      <Download className="h-4 w-4" />
                      Download DOCX
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <MarkdownEditor
              key={editorSession}
              ref={editorRef}
              value={generatedPolicy}
              onChange={() => {}} // Not used - we use explicit save
              placeholder="Generated policy will appear here..."
              height={500}
              isEditable={isEditing}
              className="bg-card"
            />
            <p className="text-xs text-muted-foreground mt-4">
              {isEditing 
                ? 'Edit the policy using the toolbar above. Click Save Changes to keep your edits, or Cancel to discard.'
                : 'Review the policy. Click Edit to make changes or Download to export as DOCX.'}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PolicyGenerator;

